import React, { useMemo } from 'react';

import { dashboardCustomsRequestApis } from '@apis/next/customsRequestApis';
import { dashboardPartnerApis } from '@apis/next/partnerApis';
import { dashboardPdfApis } from '@apis/next/pdfApis';
import { CustomsRequestSubmissionTargetInput } from '@components/admin/booking/CustomsRequestSubmissionTargetInput';
import { Button } from '@components/Button';
import {
  ActionableDialog,
  ActionableDialogActions,
  ActionableDialogTitle,
} from '@components/Dialog';
import { IndexedFormContainer, IndexedLabel } from '@components/NextForms/IndexedForm';
import { allCustomsRequestSubmissionInternalTargets } from '@data/customsRequest';
import { useFormUtils } from '@hooks/useFormUtils';
import {
  CustomsRequest,
  CustomsRequestHelper,
  CustomsRequestSubmitForm,
} from '@models/next/CustomsRequest';
import { useForm } from '@monoid-dev/mobx-zod-form-react';
import { makeMuiStyles, useCommonStyles } from '@styles/index';
import { useQueryClient } from '@tanstack/react-query';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';

const strings = createStrings({
  ja: {
    title: '送信オプション',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  dialogContent: {
    ...common.flexColumn,
    padding: '2.5rem 2rem',
    flexShrink: 1,
    minHeight: 0,
    maxHeight: 550,
  },
}));

export interface CustomsRequestSubmitDialogProps {
  customsRequest: CustomsRequest;
  open: boolean;
  onClose: () => void;
}

export const CustomsRequestSubmitDialog: React.FC<CustomsRequestSubmitDialogProps> = (props) => {
  const { customsRequest, open, onClose } = props;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const form = useForm(CustomsRequestSubmitForm, {
    initialOutput: {
      company: customsRequest.submission_target?.company ?? '',
    },
  });

  const { fields } = form.root;

  const queryClient = useQueryClient();

  const partner = dashboardPartnerApis.useGet('/dashboard/partner');

  const submissionTargets = useMemo(
    () =>
      partner.data
        ? [
            ...allCustomsRequestSubmissionInternalTargets,
            ...partner.data.map(CustomsRequestHelper.mapPartnerToSubmissionTarget),
          ]
        : allCustomsRequestSubmissionInternalTargets,
    [partner.data, allCustomsRequestSubmissionInternalTargets],
  );

  const putCustomsRequest = dashboardCustomsRequestApis.usePut(
    '/dashboard/customs-request/:d2d_no',
    {
      queries: { action: 'SUBMIT' },
      params: { d2d_no: customsRequest.d2d_no },
    },
    {
      onSuccess() {
        queryClient.invalidateQueries(
          dashboardCustomsRequestApis.getKeyByPath('get', '/dashboard/customs-request/:d2d_no'),
        );
        queryClient.invalidateQueries(
          dashboardPdfApis.getKeyByPath('get', '/dashboard/pdf/CustomsRequest/:d2d_no'),
        );
      },
    },
  );

  const { renderAlertsAndSpinnerModal } = useFormUtils({
    mutations: [putCustomsRequest],
  });

  return (
    <ActionableDialog open={open} onClose={onClose} size="md" source={import.meta.url}>
      <ActionableDialogTitle>{strings.title}</ActionableDialogTitle>

      <form
        {...form.bindForm({
          async onSubmit(values) {
            const submission_target = submissionTargets.find((t) => t.company === values.company);

            if (!submission_target) {
              throw new Error('Unexpected invalid submission target');
            }

            await putCustomsRequest.mutateAsync({ submission_target });

            onClose();
          },
        })}
      >
        <div className={classes.dialogContent}>
          <div>{renderAlertsAndSpinnerModal()}</div>

          <IndexedFormContainer size="md">
            <IndexedLabel field={fields.company} />
            <div>
              <CustomsRequestSubmissionTargetInput
                field={fields.company}
                submissionTargets={submissionTargets}
              />
            </div>
          </IndexedFormContainer>
        </div>

        <ActionableDialogActions justifyContent="right">
          <Button color="primary" size="small" text={formStrings.submit} />
        </ActionableDialogActions>
      </form>
    </ActionableDialog>
  );
};
